import {Injectable} from "@angular/core";
import {BehaviorSubject, delay, mergeMap, Observable, of, Subject, take, takeUntil} from "rxjs";
import {catchError, filter, map} from "rxjs/operators";
import { HttpEventType } from "@angular/common/http";
import {AttachmentsApiService} from "./attachments-api.service";
import {Backend} from "@generated-backend-types";
import Attachment = Backend.App.Http.Resources.Dashboard.Attachment;

enum UploadErrorCode {
  CONFLICT = 409,
  VALIDATION_ERROR = 422,
}

export interface FileUpload {
  file: File,
  attachment?: Attachment,
  path: string,
  id: string,
  progress: number;
  error: boolean;
  errorCode?: UploadErrorCode;
  errorMessage?: string;
  started: boolean;
  finished: boolean;
}


@Injectable({
  providedIn: 'root'
})
export class UploaderService {

  private uploadQueue: Subject<FileUpload> = new Subject<FileUpload>();

  public currentUploads: BehaviorSubject<{ [key: string]: FileUpload }> = new BehaviorSubject<{
    [key: string]: FileUpload
  }>({});

  private cancelUploadWithId: Subject<string> = new Subject();
  public fileUploaded: Subject<FileUpload> = new Subject();

  public uploadSummary: Observable<{ files: number, progress: number }> = this.currentUploads.pipe(
    map((u) => {
      let files = 0
      let progress = 0;

      Object.values(u).forEach((d) => {
        if (!d.finished) {
          files++;
          progress += d.progress
        }
      })
      return {
        files: files,
        progress: files === 0 ? 0 : progress / files
      }
    })
  )

  public uploadFiles(f: { files: FileList, path: string }) {
    Array.from(f.files).forEach(
      (file) => {

        this.uploadQueue.next({
          file,
          path: f.path,
          progress: 0,
          error: false,
          finished: false,
          started: false,
          id: f.path + file.name + file.lastModified
        })
      }
    )
  }

  public retry(upload: FileUpload) {
    this.uploadQueue.next({
      file: upload.file,
      path: upload.path,
      progress: 0,
      error: false,
      finished: false,
      started: false,
      id: upload.path + upload.file.name + upload.file.lastModified
    })
  }

  public cancel(upload: FileUpload) {

    this.cancelUploadWithId.next(upload.id)
    this.removeUpload(upload, 100)
  }


  private upload(d: FileUpload): Observable<FileUpload> {
    return this.attachmentsService.upload({
      path: d.path,
      name: d.file.name,
      file: d.file
    }).pipe(
      takeUntil(this.cancelUploadWithId.asObservable().pipe(filter((id) => id === d.id))),
      filter(event => {
        if (event.type == HttpEventType.UploadProgress && event.total) {
          d.progress = Math.round(event.loaded / (event.total) * 100);
          d.started = true;
          this.updateUploadStatus(d)
          return false

        } else if (event.type === HttpEventType.Response) {

          if (event.body) {
            d.progress = 100;
            d.started = true;
            d.finished = true;
            d.attachment = event.body.data;
            this.fileUploaded.next(d);
            this.updateUploadStatus(d)
            return true;
          }

        } else if (event.type === HttpEventType.Sent) {
          d.started = true;
          d.progress = 0;
          this.updateUploadStatus(d)

        }
        return false
      }),
      catchError((e) => {
        d.error = true;
        d.errorCode = e.status;
        d.errorMessage = e.error?.message;
        console.error('upload error', e.status, e.error?.message)
        this.updateUploadStatus(d)
        return of(d)
      }),
      map(() => d));
  }

  private updateUploadStatus(upload: FileUpload) {
    const current = this.currentUploads.value;
    current[upload.id] = upload
    this.currentUploads.next(current);
  }


  private removeUpload(upload: FileUpload, delayTime = 1000) {

    this.currentUploads.pipe(
      delay(delayTime),
      take(1),
    ).subscribe((current) => {
      delete current[upload.id];
      this.currentUploads.next(current);
    })

  }


  constructor(private readonly attachmentsService: AttachmentsApiService) {

    this.uploadQueue.pipe(
      mergeMap((d) => this.upload(d),
      )).subscribe(
      (upload) => {
        if (upload.error) {
          this.removeUpload(upload, 5000)
        }
        if (upload.finished) {
          this.removeUpload(upload)
        }
      }
    )
  }

}

